import { FC, useCallback, useState } from 'react'

import {
  CircleCheckmarkLineIcon,
  IconSizes,
  SquareOnSquareLineIcon
} from '@inno-ui/icons'
import { Spinner } from '@inno-ui/spinner'
import clsx from 'clsx'
import { OTPInput } from 'input-otp'
import { OtpSlot } from 'modules/auth/components'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { useEnableTwoFactor } from 'shared/api/auth/requests/2fa/enableTwoFactor'
import { useGetQrCode } from 'shared/api/auth/requests/2fa/getQrCode'
import { useUserContext } from 'shared/hooks'
import { persistTwoFaChecked } from 'shared/utils'

import { TwoFaSteps } from '../../SetupTwoFaPage.constant'
import { SetupStepsTypes } from '../../SetupTwoFaPage.types'
import { SetupTwoFaWrapper } from '../SetupTwoFaWrapper'
import styles from './SetupTwoFa.module.scss'

export const SetupTwoFa: FC<SetupStepsTypes> = ({ setCurrentStep }) => {
  const [showCopySuccessIcon, setShowCopySuccessIcon] = useState(false)
  const [otp, setOtp] = useState<string>('')
  const [hasError, setHasError] = useState<boolean>(false)

  const { t } = useTranslation('auth', {
    keyPrefix: 'setup_two_fa_view.setup_two_fa'
  })

  const { user } = useUserContext()

  const {
    query: { data: qrCodeData, isLoading }
  } = useGetQrCode()

  const onSuccess = () => {
    persistTwoFaChecked(user, 1)
    setCurrentStep(TwoFaSteps.SETUP_SUCCESS)
  }
  const onError = () => {
    setOtp('')
    setHasError(true)
  }

  const {
    mutation: { mutate: enableTwoFactorMutate, isLoading: isLoadingEnableTwoFa }
  } = useEnableTwoFactor({ onSuccess, onError })

  const onConfirm = useCallback(() => {
    enableTwoFactorMutate({ otp: otp!.toString(), secret: qrCodeData!.secret })
  }, [otp, qrCodeData, enableTwoFactorMutate])

  const handleCopyQRCode = () => {
    navigator.clipboard.writeText(qrCodeData?.secret ?? '')
    setShowCopySuccessIcon(true)
    setTimeout(() => {
      setShowCopySuccessIcon(false)
    }, 2000)
  }

  const handleOtpChange = (value: string) => {
    setOtp(value)
    if (value) {
      setHasError(false)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
    }
  }

  return (
    <SetupTwoFaWrapper
      title={t('title')}
      primaryButtonLabel={t('submit_button')}
      onPrimaryButtonClick={onConfirm}
      currentStep={TwoFaSteps.SETUP_TWO_FA}
      isLoading={isLoadingEnableTwoFa}
      isDisabled={
        otp.length !== 6 || isLoadingEnableTwoFa || !qrCodeData?.secret
      }
    >
      <main className={styles.cardsWrapper}>
        <section className={styles.qrCodeCard}>
          <ReactMarkdown>{t('step_one_description')}</ReactMarkdown>
          <figure className={styles.qrCode}>
            {isLoading ? <Spinner /> : <img alt={'qr'} src={qrCodeData?.qr} />}
          </figure>
          <div className={styles.manualEntryKeyWrapper}>
            <p className={styles.manualEntryKeyLabel}>
              {t('manual_key_label')}
            </p>
            <div className={styles.manualEntryKey}>
              {qrCodeData?.secret}{' '}
              {showCopySuccessIcon ? (
                <CircleCheckmarkLineIcon
                  size={IconSizes.Size16}
                  color='var(--white-t50)'
                />
              ) : (
                <button
                  onClick={handleCopyQRCode}
                  className={styles.copyIcon}
                  type='button'
                  aria-label='Copy secret key'
                >
                  <SquareOnSquareLineIcon
                    size={IconSizes.Size16}
                    color='var(--white-t50)'
                  />
                </button>
              )}
            </div>
          </div>
        </section>

        <section className={styles.otpCard}>
          <ReactMarkdown>{t('step_two_description')}</ReactMarkdown>
          <div
            className={styles.otpInput}
            role='group'
            aria-label='Enter verification code'
          >
            <OTPInput
              value={otp}
              onChange={handleOtpChange}
              onKeyDown={handleKeyDown}
              containerClassName={clsx(styles.slots, {
                [styles.shake]: hasError
              })}
              type='number'
              maxLength={6}
              min={0}
              render={({ slots }) => {
                return slots.map((slot, i) => (
                  <OtpSlot
                    hasError={hasError}
                    key={i}
                    {...slot}
                    isActive={slot.isActive || i === otp.length}
                    isLoading={isLoadingEnableTwoFa}
                    className={styles.otpSlot}
                  />
                ))
              }}
            />
          </div>
        </section>
      </main>
    </SetupTwoFaWrapper>
  )
}
